'use client'

import type { PayloadAdminBarProps, PayloadMeUser } from '@payloadcms/admin-bar'

import { cn } from '@/utilities/ui'
import { useSelectedLayoutSegments } from 'next/navigation'
import { PayloadAdminBar } from '@payloadcms/admin-bar'
import React, { useState } from 'react'
import { useRouter } from 'next/navigation'

import './index.scss'

import { getClientSideURL } from '@/utilities/getURL'

import type { GeoBarState } from './geo'
import { GeoControls } from './GeoControls'

const baseClass = 'admin-bar'

const collectionLabels = {
  pages: {
    plural: 'Pages',
    singular: 'Page',
  },
  posts: {
    plural: 'Posts',
    singular: 'Post',
  },
  projects: {
    plural: 'Projects',
    singular: 'Project',
  },
}

const Title: React.FC = () => <span>Dashboard</span>

/**
 * AdminBar provides a contextual admin toolbar for Payload CMS authenticated users
 * 
 * @description
 * The AdminBar component renders a top-level administrative toolbar that appears only for 
 * authenticated Payload CMS users. It provides quick access to content editing controls, 
 * live preview functionality, and collection management based on the current page context.
 * 
 * The component automatically detects the current collection type (pages, posts, projects) 
 * from the URL segments and provides appropriate edit controls. It integrates seamlessly 
 * with Payload's live preview system, allowing authenticated users to preview unpublished 
 * content and exit preview mode.
 * 
 * Key features:
 * - Contextual collection detection and labeling
 * - Live preview controls with exit functionality
 * - Authentication-based visibility (hidden for anonymous users)
 * - Responsive design with consistent styling
 * - Integration with Next.js App Router navigation
 * 
 * @example
 * ```tsx
 * // Basic usage in layout (most common)
 * import { AdminBar } from '@/components/AdminBar'
 * 
 * export default function RootLayout({ children }) {
 *   return (
 *     <html>
 *       <body>
 *         <AdminBar />
 *         {children}
 *       </body>
 *     </html>
 *   )
 * }
 * ```
 * 
 * @example
 * ```tsx
 * // With preview mode enabled
 * import { AdminBar } from '@/components/AdminBar'
 * import { draftMode } from 'next/headers'
 * 
 * export default function Layout({ children }) {
 *   const { isEnabled } = draftMode()
 *   
 *   return (
 *     <div>
 *       <AdminBar
 *         adminBarProps={{
 *           preview: isEnabled,
 *         }}
 *       />
 *       {children}
 *     </div>
 *   )
 * }
 * ```
 * 
 * @example
 * ```tsx
 * // In a page component with collection context
 * // The AdminBar automatically detects the collection from URL:
 * // - /pages/[slug] → "pages" collection
 * // - /posts/[slug] → "posts" collection  
 * // - /projects/[slug] → "projects" collection
 * // - Default fallback → "pages" collection
 * ```
 */
export interface AdminBarProps {
  /** Optional props to pass to the underlying PayloadAdminBar component */
  adminBarProps?: PayloadAdminBarProps
  geo?: GeoBarState | null
}

export const AdminBar: React.FC<AdminBarProps> = (props) => {
  const { adminBarProps, geo } = props || {}
  const segments = useSelectedLayoutSegments()
  const [show, setShow] = useState(false)
  const collection = (
    collectionLabels[segments?.[1] as keyof typeof collectionLabels] ? segments[1] : 'pages'
  ) as keyof typeof collectionLabels
  const router = useRouter()

  const onAuthChange = React.useCallback((user: PayloadMeUser) => {
    setShow(Boolean(user?.id))
  }, [])

  return (
    <div
      className={cn(baseClass, 'py-2 bg-black text-white', {
        block: show,
        hidden: !show,
      })}
    >
      {/* The bar's own box is display: contents, so GeoControls can sit between the email and the buttons */}
      <div className="container flex items-center" style={{ fontSize: 'small' }}>
        <PayloadAdminBar
          {...adminBarProps}
          adminPath="/cms"
          apiPath="/cms/api"
          className="text-white"
          classNames={{
            controls: 'order-last !grow-0 font-medium text-white',
            logo: 'text-white',
            user: 'text-white',
          }}
          cmsURL={getClientSideURL()}
          collectionSlug={collection}
          collectionLabels={{
            plural: collectionLabels[collection]?.plural || 'Pages',
            singular: collectionLabels[collection]?.singular || 'Page',
          }}
          logo={<Title />}
          onAuthChange={onAuthChange}
          onPreviewExit={() => {
            fetch('/next/exit-preview').then(() => {
              router.push('/')
              router.refresh()
            })
          }}
          style={{
            backgroundColor: 'transparent',
            display: 'contents',
            padding: 0,
          }}
        />
        {geo && <GeoControls {...geo} />}
      </div>
    </div>
  )
}
